<template>
  <!-- Item not found -->
  <ItemNotFoundState v-if="objectNotFound" :objectName="'Obrazovka'">
    <template #backButton>
      <BackButton :target="{ name: 'unitDetail' }">Informační panely</BackButton>
    </template>
  </ItemNotFoundState>
  <Loading v-else-if="!didLoadData" />
  <div v-else>
    <!-- Module header -->
    <BackButton :target="{ name: 'unitDetail' }">Informační panely</BackButton>
    <ModuleHeader>
      <div id="header-title">
        <h2>{{ `${isNew ? 'Přidání' : 'Úprava'} obrazovky` }}</h2>
      </div>
      <v-spacer />
      <SaveButton
        class="ml-5 mb-6"
        :disabled="!isFormValid"
        :is-saving="isSaving"
        @save="saveDetail"
      />
    </ModuleHeader>
    <!-- Display detail -->
    <div class="mb-5 mt-5">
      <v-form @submit.prevent="saveDetail" v-model="isFormValid" ref="form">
        <div class="v-data-table v-data-table--fixed-header">
          <div class="v-data-table__wrapper">
            <table>
              <tbody>
                <tr v-if="!isNew">
                  <th scope="row" class="table-header">ID</th>
                  <td>
                    <v-text-field
                      color="dark"
                      :disabled="true"
                      label="Nelze měnit"
                      :value="displayId"
                    />
                  </td>
                </tr>
                <tr>
                  <th scope="row" class="table-header">Název</th>
                  <td>
                    <v-text-field
                      :autofocus="isNew"
                      id="display-name"
                      color="dark"
                      :label="`${display.name.length}/${displaysConstants.display.name} znaků`"
                      :maxlength="displaysConstants.display.name"
                      :rules="[required_rule]"
                      v-model="display.name"
                      @change="changeWatch"
                    />
                  </td>
                </tr>
                <tr>
                  <th scope="row" class="table-header">Orientace</th>
                  <td>
                    <v-select
                        id="display-orientation"
                        :disabled="!isNew"
                        color="dark"
                        :items="orientationTypes"
                        item-title="title"
                        item-value="value"
                        label="Orientace obrazovky"
                        :rules="[required_rule]"
                        :model-value="display.orientation"
                        @update:model-value="value => changeOrientation(value)"
                    />
                  </td>
                </tr>
                <tr>
                  <th scope="row" class="table-header">Počet oznámení</th>
                  <td>
                    <v-select
                      id="display-parallels"
                      color="dark"
                      @change="changeWatch"
                      :items="displaysConstants.display.maxParallels"
                      label="Počet paralelně zobrazovaných oznámení"
                      :rules="[required_rule]"
                      v-model="display.maxParallels"
                      :disabled="isMaxParallelsDisabled"
                    />
                  </td>
                </tr>
                <tr>
                  <th scope="row" class="table-header">
                    Maják
                    <InfoButton right small>
                      Slouží k rozpoznání obrazovky mobilním zařízením s aplikací
                      Moje MENDELU v jeho blízkosti. Pro výběr musí být maják
                      předdefinován v modulu "Majáky".
                    </InfoButton>
                  </th>
                  <td>
                    <v-autocomplete
                      id="display-beacons"
                      color="dark"
                      :items="beacons"
                      label="Přiřazený maják"
                      item-title="beaconId"
                      item-value="id"
                      :value="display.beaconId"
                      @change="changeBeacon"
                    />
                  </td>
                </tr>
                <tr>
                  <th scope="row" class="table-header pr-2">
                    Perioda aktualizace obsahu [s]
                  </th>
                  <td>
                    <v-text-field
                      type="number"
                      id="display-period"
                      label="Počet vteřin"
                      v-model="display.refreshPeriod"
                      :rules="[required_rule, period_rule]"
                      :max="periodConstants.max"
                      :min="periodConstants.min"
                      append-icon="add"
                      prepend-icon="remove"
                      @click:append="() => changePeriod(1)"
                      @click:prepend="() => changePeriod(-1)"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </v-form>
    </div>
  </div>
</template>

<script setup>
import {ref, reactive, computed, onMounted} from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import BackButton from "../../components/layout/BackButton";
import ItemNotFoundState from "../../components/states/ItemNotFoundState.vue";
import Loading from "../../components/Loading";
import ModuleHeader from "../../components/layout/ModuleHeader";
import InfoButton from "../../components/buttons/InfoButton";
import SaveButton from "../../components/buttons/SaveButton";
import {displaysMaxValues, LANDSCAPE, PORTRAIT} from "@/config";

const store = useStore();
const route = useRoute();
const router = useRouter();

const periodConstants = displaysMaxValues.display.refreshPeriod;

const isSaving = ref(false);
const isFormValid = ref(true);
const beacons = computed(() => store.getters["Beacons/beacons"]);
const objectNotFound = computed(() => store.getters["errors/objectNotFound"]);
const displaysConstants = displaysMaxValues;
const orientationTypes = [{title: 'Na šířku', value: LANDSCAPE},{title: 'Na výšku', value: PORTRAIT}]

const required_rule = (value) => !!value || "Povinné";
const period_rule = (value) => {
  const val = Number.parseInt(value);
  return (
    (val && val >= periodConstants.min && val <= periodConstants.max) ||
    `Zadejte číslo od ${periodConstants.min} do ${periodConstants.max}`
  );
};

const display = reactive({
  name: "",
  orientation: LANDSCAPE,
  maxParallels: undefined,
  beaconId: undefined,
  refreshPeriod: 5,
});

const displayId = computed(() => {
  const urlId = route.params.displayId;
  return urlId === "new" ? null : Number(urlId);
});
const isNew = computed(() => displayId.value === null);
const didLoadData = computed(
  () =>
    store.getters["Beacons/didLoadBeacons"] &&
    (store.getters["displays/didLoadDisplays"] || isNew.value)
);

const changeWatch = () => {
  store.dispatch("leavePrevent/madeUnsavedChanges");
};

const changeBeacon = (value) => {
  changeWatch();
  display.beaconId = value;
};

const saveDetail = async () => {
  if (isFormValid.value) {
    isSaving.value = true;
    if (isNew.value) {
      await store.dispatch("displays/createDisplay", {
        ...display,
        unitId: route.params.unitId,
      });
      isSaving.value = false;
      store.dispatch("leavePrevent/didSaveChanges");
      router.push({ name: "unitDetail" });
    } else {
      await store.dispatch("displays/updateDisplay", display);
      isSaving.value = false;
      store.dispatch("leavePrevent/didSaveChanges");
    }
  }
};

onMounted(async () => {
  await store.dispatch("Beacons/fetchBeacons");
  if (!isNew.value) {
    await store.dispatch("displays/fetchDisplayById", displayId.value);
    const initialDisplay = store.getters['displays/getDisplayByID'](displayId.value);
    Object.assign(display, initialDisplay);
  } else {
    const newDisplay = store.getters['display/prepareNewDisplay'];
    Object.assign(display, newDisplay);
    store.dispatch("errors/clear");
  }
});

const changePeriod = (value) => {
  const currentValue = Number.parseInt(display.refreshPeriod);
  display.refreshPeriod =
    currentValue &&
    (value > 0
      ? currentValue >= periodConstants.min
      : currentValue > periodConstants.min)
      ? Math.min(currentValue + value, periodConstants.max)
      : periodConstants.min;
};

const changeOrientation = (value) => {
  display.orientation = value
  if(value === PORTRAIT){
    display.maxParallels = 1
  }
}

const isMaxParallelsDisabled = computed(() => display.maxParallels === 1 && display.orientation === PORTRAIT)
</script>

<style scoped>
li {
  list-style-type: none;
}

.table-header {
  font-size: 16px;
}
</style>
